import { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useToast } from "../ToastContext";

export default function TeamMembers({ user, plan }) {
  const { addToast } = useToast();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [inviting, setInviting] = useState(false);

  const isPro = plan === "pro" || plan === "agency";

  useEffect(() => {
    if (user?.id) fetchMembers();
  }, [user?.id]);

  const fetchMembers = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("team_members")
      .select("*")
      .eq("owner_id", user.id)
      .order("invited_at", { ascending: false });
    setMembers(data || []);
    setLoading(false);
  };

  const handleInvite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    try {
      const { error } = await supabase.from("team_members").insert([{
        owner_id: user.id,
        member_email: email.trim().toLowerCase(),
        role,
        status: "pending"
      }]);
      if (error) {
        if (error.code === "23505") addToast("This email is already invited", "error");
        else throw error;
      } else {
        addToast("Invitation sent!", "success");
        setEmail("");
        fetchMembers();
      }
    } catch (err) {
      addToast(err.message, "error");
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    await supabase.from("team_members").delete().eq("id", id);
    setMembers(prev => prev.filter(m => m.id !== id));
    addToast("Member removed", "success");
  };

  const handleRoleChange = async (id, newRole) => {
    await supabase.from("team_members").update({ role: newRole }).eq("id", id);
    setMembers(prev => prev.map(m => m.id === id ? { ...m, role: newRole } : m));
    addToast("Role updated", "success");
  };

  if (!isPro) return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl mb-4">👥</div>
        <h2 className="text-xl font-black text-white uppercase italic tracking-tighter mb-2">Team Access</h2>
        <p className="text-gray-500 text-sm mb-6">Invite team members to collaborate on your QR codes</p>
        <p className="text-[10px] text-red-400 uppercase font-black">Available on Pro & Agency plans</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-black text-white uppercase italic tracking-tighter">Team Members</h1>
        <p className="text-gray-500 text-[10px] uppercase tracking-widest mt-1">Invite collaborators to view or manage your QR codes</p>
      </div>

      {/* Invite form */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
        <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest">Invite New Member</p>
        <div className="flex gap-3 flex-wrap">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleInvite()}
            placeholder="colleague@company.com"
            className="flex-1 bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-blue-500 min-w-48"
          />
          <select
            value={role}
            onChange={e => setRole(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none"
          >
            <option value="viewer">Viewer — can see analytics only</option>
            <option value="editor">Editor — can edit QR codes</option>
          </select>
          <button
            onClick={handleInvite}
            disabled={inviting || !email}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-xl text-[10px] font-black uppercase text-white transition-all"
          >
            {inviting ? "Inviting..." : "Send Invite"}
          </button>
        </div>
        <p className="text-[9px] text-gray-600">Invited members will appear here. They need to sign up with the same email to get access.</p>
      </div>

      {/* Members list */}
      <div className="bg-white/5 border border-white/10 rounded-[32px] overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <h3 className="text-sm font-black uppercase tracking-widest">Team ({members.length})</h3>
        </div>
        {loading ? (
          <div className="p-10 flex justify-center">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm">No team members yet. Invite someone above.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {members.map(m => (
              <div key={m.id} className="flex items-center justify-between px-6 py-4">
                <div>
                  <p className="text-white text-sm font-black">{m.member_email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${m.status === "active" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                      {m.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={m.role}
                    onChange={e => handleRoleChange(m.id, e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg p-2 text-white text-[10px] outline-none"
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </select>
                  <button
                    onClick={() => handleRemove(m.id)}
                    className="text-red-500 hover:text-red-400 text-[10px] font-black uppercase"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}